/** A circle: a small private group from one of a room's groups, live with Maya or talking after, with the next call beside it. */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { wallClock, wallTimeToUtc } from "../../shared/live-time";
import type { HostCircleResponse } from "../../shared/talk";
import type { Attachment } from "../../shared/types";
import { dayLabel, startTimeGroups, todayIn } from "../board/liveForm";
import { DaySheet } from "../components/DaySheet";
import { Button } from "../components/ui/Button";
import { Face, FaceStack } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Select } from "../components/ui/Select";
import { Sheet } from "../components/ui/Sheet";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { homeZone } from "../lib/zones";
import { AskSheet } from "./AskSheet";
import { ChatList, RemoveSheet, useLineTools, useStick } from "./Chat";
import { Composer } from "./Composer";
import { facesOf, Head } from "./parts";
import { Nav } from "./phone";
import { useHost } from "./useHost";
import { useTalkContext } from "./TalkLayout";
import { useRemote, useSeenUpTo, useTypers } from "./useTalk";
import { callDay, callLine, clockTime, comingWords, firstName, madeWords, peopleWords, sinceWords } from "./when";
import { pinWords } from "../../shared/talk-words";

type Live = NonNullable<HostCircleResponse["live"]>;

const studioOf = (live: Live) => `/rooms/${live.webinarId}/studio`;
const LENGTHS = [15, 20, 30, 45, 60];

/** Plan or move the circle's next call. */
function NextCallSheet({ cid, call, onClose, onSaved }: { cid: string; call: HostCircleResponse["nextCall"]; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const zone = homeZone();
  const was = call ? wallClock(new Date(call.startsAt * 1000).toISOString(), zone) : null;
  const [date, setDate] = useState(was?.date ?? "");
  const [time, setTime] = useState(was?.time ?? "");
  const [picking, setPicking] = useState(false);
  const [len, setLen] = useState(call?.lengthMin ?? 20);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const iso = date && time ? wallTimeToUtc(date, time, zone) : null;
    if (!iso) {
      toast({ text: "Pick the day and time first", icon: "info" });
      return;
    }
    setBusy(true);
    try {
      await api.talk.nextCall(cid, { startsAt: Math.floor(Date.parse(iso) / 1000), lengthMin: len });
      onSaved();
      onClose();
    } catch {
      toast({ text: "We couldn't save the time. Try again.", icon: "info" });
      setBusy(false);
    }
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title={call ? "Change the time" : "Plan the next call"}
      footer={
        <>
          <Button onClick={onClose}>Not now</Button>
          <Button variant="p" icon="cal" disabled={busy} onClick={() => void save()}>
            Save the time
          </Button>
        </>
      }
    >
      <div className="sheet-b hp-ctl">
        <div className="field">
          <span className="lbl">Day</span>
          <button type="button" className="inp mk-day" aria-label={`Day, ${date ? dayLabel(date) : "pick one"}`} onClick={() => setPicking(true)}>
            <Icon name="cal" />
            <span>{date ? dayLabel(date) : "Pick a day"}</span>
          </button>
        </div>
        <div className="field">
          <span className="lbl">Starts</span>
          <Select ariaLabel="Starts" placeholder="Pick a time" value={time} suggested="12:00" options={startTimeGroups(date, zone)} onChange={setTime} />
        </div>
        <div className="field">
          <span className="lbl">How long</span>
          <Select ariaLabel="How long" value={String(len)} options={LENGTHS.map((n) => ({ value: String(n), label: `${n} min` }))} onChange={(v) => setLen(Number(v))} />
        </div>
      </div>
      {picking ? <DaySheet value={date} min={todayIn(zone)} onPick={setDate} onClose={() => setPicking(false)} /> : null}
    </Sheet>
  );
}

function CircleSide({ d, onTime }: { d: HostCircleResponse; onTime: () => void }) {
  const tagOf = (id: string) => d.members.find((m) => m.registrantId === id)?.tag ?? null;
  if (d.live) {
    return (
      <>
        <h3>In the call · {d.live.joined.length}</h3>
        {d.live.joined.map((w) => (
          <div key={w.registrantId} className="mem">
            <span className="on-f">
              <Face name={w.name} photo={w.photoUrl} size={32} />
              <i className="red" />
            </span>
            <span className="mem-t">
              <b>{w.name}</b>
              <span>{tagOf(w.registrantId)}</span>
            </span>
          </div>
        ))}
        <h3>On their way · {d.live.onTheirWay.length}</h3>
        {d.live.onTheirWay.map((w) => (
          <div key={w.registrantId} className="mem dim">
            <Face name={w.name} photo={w.photoUrl} size={32} />
            <span className="mem-t">
              <b>{w.name}</b>
              <span>{tagOf(w.registrantId)}</span>
            </span>
          </div>
        ))}
      </>
    );
  }
  const call = d.nextCall;
  return (
    <>
      <div className="side-card">
        <small>Next call</small>
        {call ? (
          <>
            <b>{callDay(call.startsAt)}</b>
            <span>
              {clockTime(call.startsAt)} · {call.lengthMin} min
            </span>
            <span className="side-card-who">
              <FaceStack people={facesOf(call.comingFaces.slice(0, 4))} size={24} more={Math.max(0, call.coming - 4)} />
              <em>{comingWords(call.coming)}</em>
            </span>
          </>
        ) : (
          <b>None planned yet</b>
        )}
        <Button size="s" icon="cal" onClick={onTime}>
          {call ? "Change the time" : "Plan the next call"}
        </Button>
      </div>
      <h3>Members · {d.circle.members}</h3>
      {d.members.map((w) => (
        <div key={w.registrantId} className="mem">
          <Face name={w.name} photo={w.photoUrl} size={32} />
          <span className="mem-t">
            <b>{w.name}</b>
            <span>{w.tag}</span>
          </span>
        </div>
      ))}
    </>
  );
}

export function CircleScreen() {
  const { cid = "" } = useParams();
  const ctx = useTalkContext();
  const phone = ctx.lay === "phone";
  const upright = ctx.lay === "upright";
  const navigate = useNavigate();
  const toast = useToast();
  const me = useHost();
  const typers = useTypers();
  const nameOf = useRef<(rid: string | undefined) => string | undefined>(() => undefined);
  const { data, phase, reload, setData, ping } = useRemote(`talk:circle:${cid}`, () => api.talk.circle(cid), `/talk/circles/${encodeURIComponent(cid)}/ws`, (e) => typers.hear(e));
  nameOf.current = (rid) => data?.members.find((m) => m.registrantId === rid)?.name;
  useSeenUpTo(data ? `circle:${cid}` : null, Math.max(0, ...(data?.messages.map((m) => m.createdAt) ?? [])), ctx.reloadRail);
  const seen = useRef<{ cid: string; at: number | null } | null>(null);
  if (data && seen.current?.cid !== cid) seen.current = { cid, at: data.seenAt };
  const [asking, setAsking] = useState(false);
  const [info, setInfo] = useState(false);
  const [time, setTime] = useState(false);
  const [pin, setPin] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [going, setGoing] = useState(false);
  const scroll = useRef<HTMLDivElement>(null);
  const onScroll = useStick(scroll, data?.messages.length ?? 0, cid);
  const tools = useLineTools({
    webinarId: null,
    reload: () => void reload(),
    patch: (change, id) => setData((d) => (d ? { ...d, messages: d.messages.map((m) => (m.id === id ? change(m) : m)) } : d)),
  });
  useEffect(() => {
    if (data) ctx.reloadRail();
  }, [data, ctx.reloadRail]);

  if (!data) return phase === "error" ? <main className="main"><p className="empty-note">We couldn't open this circle. Pick it again.</p></main> : <main className="main" />;
  const { circle, live, nextCall } = data;
  const host = { name: me.name, photo: data.messages.find((m) => m.who.kind === "host")?.who.photoUrl ?? me.photo };
  const pinned = data.messages.filter((m) => m.pinned).toSorted((a, b) => a.createdAt - b.createdAt).at(-1);
  const made = circle.madeFromWords ? ` · ${madeWords(circle.madeFromWords)}` : "";
  const sub = (upright || phone) && nextCall ? `Next call ${callLine(nextCall.startsAt)}` : `${peopleWords(circle.members)}${made}`;

  const goLive = async () => {
    if (going) return;
    setGoing(true);
    try {
      const made = await api.talk.goLive(cid);
      navigate(made.studioPath);
    } catch {
      toast({ text: "We couldn't go live. Try again.", icon: "info" });
      setGoing(false);
    }
  };
  const send = async (out: { text: string; attachment: Attachment | null }) => {
    setNotice(null);
    try {
      await api.talk.sendCircle(cid, { body: out.text, attachments: out.attachment ? [out.attachment] : undefined, clientId: crypto.randomUUID(), replyTo: tools.replyTo?.id, pin: pin || undefined });
      tools.setReplyTo(null);
      setPin(false);
      await reload();
    } catch (err) {
      setNotice(err instanceof Error && err.message ? err.message : "We couldn't send that. Try again.");
      throw err;
    }
  };
  const ask = async (q: { text: string; options: string[] }) => {
    await api.talk.sendCircle(cid, { body: q.text, clientId: crypto.randomUUID(), poll: { question: q.text, options: q.options } });
    setAsking(false);
    toast({ text: "Sent", icon: "send" });
    await reload();
  };
  const watch = (m: { call: { webinarId: string; liveSessionId: string } | null }) => {
    if (m.call) navigate(`/rooms/${m.call.webinarId}/sessions/${m.call.liveSessionId}`);
  };
  const replying = tools.replyTo ? { name: firstName(tools.replyTo.who.name), text: tools.replyTo.body, onClear: () => tools.setReplyTo(null) } : null;
  const composer = (
    <Composer
      to="the circle"
      phone={phone}
      host={host}
      send="Send"
      placeholder={phone ? "Write to the circle" : "Write to the circle..."}
      away={asking}
      onAsk={live ? undefined : () => setAsking(true)}
      onSend={send}
      onTyping={ping}
      notice={notice}
      replying={replying}
      checks={
        live || phone ? undefined : (
          <label className="cmp-pin">
            <input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} /> Pin it
          </label>
        )
      }
    />
  );
  const joinedFaces = live ? facesOf(live.joined.slice(0, phone ? 4 : 6)) : [];
  const lead = live ? (
    <p className="sysline">
      <Icon name="live" /> You went live at {clockTime(live.startedAt)} · {phone ? circle.members.toLocaleString("en-US") : peopleWords(circle.members)} {circle.members === 1 ? "was" : "were"} told
    </p>
  ) : null;
  const hello =
    data.messages.length === 0 && !live ? (
      <p className="empty-note">Say hello to the circle. {circle.members === 1 ? "Only you see this." : `Only the ${circle.members.toLocaleString("en-US")} of you see this.`}</p>
    ) : null;
  const list = (
    <ChatList
      className={phone ? "hp-chat" : "chat"}
      scroll={scroll}
      onScroll={onScroll}
      messages={data.messages}
      seenAt={seen.current?.at ?? null}
      tools={tools}
      phone={phone}
      host={host}
      members={circle.members}
      typing={typers.who((rid) => nameOf.current(rid))}
      lead={lead ?? hello}
      onWatch={watch}
    />
  );
  const sheets = (
    <>
      <AskSheet open={asking} to="the circle" onClose={() => setAsking(false)} onSend={ask} />
      <RemoveSheet tools={tools} />
      {time ? <NextCallSheet cid={cid} call={nextCall} onClose={() => setTime(false)} onSaved={() => void reload()} /> : null}
      <Sheet open={info} onClose={() => setInfo(false)} title={circle.name}>
        <div className="sheet-b side-sheet">
          <CircleSide d={data} onTime={() => (setInfo(false), setTime(true))} />
        </div>
      </Sheet>
    </>
  );
  const liveLine = live ? `${data.live?.joined.length} ${phone ? "in" : "joined"} · ${live.onTheirWay.length} on their way` : "";

  if (phone) {
    return (
      <div className="hp">
        <Nav
          back="Rooms"
          title={circle.name}
          sub={live ? peopleWords(circle.members) : sub}
          onSub={() => setInfo(true)}
          subLabel="About this circle"
          right={
            live ? null : (
              <button type="button" className="hp-golive" disabled={going} onClick={() => void goLive()}>
                <Icon name="live" /> Go live
              </button>
            )
          }
        />
        {live ? (
          <div className="hp-live">
            <span className="lb-pulse">
              <i />
              Live
            </span>
            <span className="hp-live-t">
              <b>{sinceWords(live.startedAt)}</b>
              <small>{liveLine}</small>
            </span>
            <FaceStack people={joinedFaces} size={24} />
            <button type="button" className="hp-live-b" onClick={() => navigate(studioOf(live))}>
              Studio
            </button>
          </div>
        ) : null}
        {pinned && !live ? (
          <div className="hp-pin">
            <Icon name="pin" />
            <span>
              <b>{firstName(pinned.who.name)}:</b> {pinWords(pinned)}
            </span>
          </div>
        ) : null}
        {list}
        {composer}
        {sheets}
      </div>
    );
  }
  return (
    <>
      <main className="main">
        <Head
          title={circle.name}
          sub={sub}
          onInfo={upright ? () => setInfo(true) : undefined}
          infoLabel="About this circle"
          right={
            <>
              {live ? null : (
                <Button variant="p" icon="live" disabled={going} onClick={() => void goLive()}>
                  Go live with the circle
                </Button>
              )}
            </>
          }
        />
        {live ? (
          <div className="livebar">
            <span className="lb-pulse">
              <i />
              Live
            </span>
            <span className="lb-t">
              <b>You are live with the circle</b>
              <small>
                {sinceWords(live.startedAt)} · {liveLine}
              </small>
            </span>
            {upright ? <FaceStack people={joinedFaces} size={26} /> : null}
            <Button variant="p" icon="live" onClick={() => navigate(studioOf(live))}>
              Back to the studio
            </Button>
          </div>
        ) : pinned ? (
          <div className="pinbar">
            <Icon name="pin" />
            <span>
              <b>{firstName(pinned.who.name)}:</b> {pinWords(pinned)}
            </span>
            {pinned.hearts ? (
              <span className="react">
                <Icon name="heart" /> {pinned.hearts}
              </span>
            ) : null}
          </div>
        ) : null}
        {list}
        {composer}
      </main>
      <aside className="side">
        <CircleSide d={data} onTime={() => setTime(true)} />
      </aside>
      {sheets}
    </>
  );
}
